import { api } from "@/lib/api/client";
import { partnerEndpoints } from "@/lib/api/endpoints";
import type { DocumentCategory, DocumentChecklistItem } from "@/types/application";

export type DocumentMapping = {
  id: number;
  business_type: number | null;
  document_type: number;
  document_type_key: string;
  category: DocumentCategory;
  is_mandatory: boolean;
  multi_upload: boolean;
  title: string;
  description?: string | null;
  /** Only set for the audited financials mapping. */
  financial_year_options?: string[] | null;
  minimum_financial_years?: number | null;
  required_financial_years?: string[] | null;
  /** Loan amount above which this document becomes mandatory (null when unconditional). */
  mandatory_above_loan_amount?: number | null;
  /** Type of financing for which this document becomes mandatory (null when unconditional). */
  mandatory_when_financing?: string | null;
};

export const DOC_KEY_BANK_STATEMENTS = "bank_statements";
export const DOC_KEY_INVOICES = "invoices";
export const DOC_KEY_AECB_CREDIT_REPORT = "aecb_credit_report";
export const DOC_KEY_AUDITED_FINANCIALS = "audited_financials";
export const DOC_KEY_VAT_RETURNS = "vat_returns";
export const DOC_KEY_ADDITIONAL_DOCUMENTS = "additional_documents";
export const DOC_KEY_RECEIVABLES_AGEING = "receivables_ageing";
export const DOC_KEY_BUSINESS_CONTRACTS = "business_contracts";
export const DOC_KEY_BUYER_INVOICES = "buyer_invoices";

export type ApplicationDocumentFile = {
  uuid: string;
  file_name: string;
  file_url?: string | null;
  uploaded_at?: string | null;
  uploaded_by?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type ApplicationDocument = {
  uuid: string;
  document_type: number;
  document_key: string;
  document_type_title: string;
  status: string;
  shareholder?: number | null;
  metadata?: Record<string, unknown> | null;
  document_files: ApplicationDocumentFile[];
};

export async function listDocumentMappings(params: {
  business_type_id?: number | string | null;
  category?: DocumentCategory;
  document_key?: string;
}): Promise<DocumentMapping[]> {
  const query: Record<string, string | number> = {};
  if (params.business_type_id) query.business_type_id = params.business_type_id;
  if (params.category) query.category = params.category;
  if (params.document_key) query.document_key = params.document_key;

  const data = await api.get<DocumentMapping[] | { results: DocumentMapping[] }>(
    partnerEndpoints.documentTypes,
    { params: query },
  );

  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
}

export async function listApplicationDocuments(
  applicationUuid: string,
): Promise<ApplicationDocument[]> {
  const data = await api.get<ApplicationDocument[] | { results: ApplicationDocument[] }>(
    partnerEndpoints.documents(applicationUuid),
  );
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
}

export function buildChecklist(
  mappings: DocumentMapping[],
  documents: ApplicationDocument[],
): DocumentChecklistItem[] {
  const byTypeId = new Map<number, ApplicationDocument>();
  for (const doc of documents) {
    byTypeId.set(doc.document_type, doc);
  }

  return mappings.map((mapping) => {
    const uploaded = byTypeId.get(mapping.document_type);
    const files = (uploaded?.document_files || []).map((f) => ({
      uuid: f.uuid,
      file_name: f.file_name,
      file_url: f.file_url ?? null,
      uploaded_at: f.uploaded_at ?? null,
      uploaded_by: f.uploaded_by ?? null,
    }));
    return {
      document_key: mapping.document_type_key,
      document_type_id: mapping.document_type,
      document_uuid: uploaded?.uuid ?? null,
      label: mapping.title,
      description: mapping.description ?? null,
      category: mapping.category,
      required: mapping.is_mandatory,
      multi_upload: Boolean(mapping.multi_upload),
      uploaded: files.length > 0,
      status: uploaded?.status ?? null,
      files,
      file_name: files[0]?.file_name ?? null,
      uploaded_at: files[0]?.uploaded_at ?? null,
    };
  });
}

/** Personal/shareholder KYC checklist — mirrors admin get_personal_documents rules. */
export function buildPersonalChecklist(
  mappings: DocumentMapping[],
  documents: ApplicationDocument[],
  opts: {
    shareholderId: number;
    shareholderType: "INDIVIDUAL" | "BUSINESS";
    isUaeResident: boolean;
  },
): DocumentChecklistItem[] {
  const shareholderDocs = documents.filter((doc) => doc.shareholder === opts.shareholderId);
  let filtered = mappings.filter((m) => m.category === "personal_kyc");

  if (opts.shareholderType === "BUSINESS") {
    filtered = filtered.filter((m) => m.document_type_key === "company_id");
  } else {
    filtered = filtered.filter((m) => m.document_type_key !== "company_id");
  }

  return buildChecklist(filtered, shareholderDocs).map((item) => {
    let required = false;
    if (opts.shareholderType === "BUSINESS") {
      required = item.document_key === "company_id";
    } else if (item.document_key === "passport") {
      required = true;
    } else if (item.document_key === "emirates_id") {
      required = opts.isUaeResident;
    }
    return { ...item, required, multi_upload: true };
  });
}

/** The financial year a container belongs to, or "" when unset/invalid. */
export function financialYearOfDocument(doc: ApplicationDocument): string {
  const value = doc.metadata?.financial_year;
  return typeof value === "string" ? value : "";
}

/** Distinct financial years selected, and the subset with an uploaded file. */
export function summarizeFinancialYears(documents: ApplicationDocument[]): {
  selected: string[];
  uploaded: string[];
} {
  const selected = new Set<string>();
  const uploaded = new Set<string>();
  for (const doc of documents) {
    const year = financialYearOfDocument(doc);
    if (!year) continue;
    selected.add(year);
    if ((doc.document_files || []).length > 0) uploaded.add(year);
  }
  return { selected: Array.from(selected), uploaded: Array.from(uploaded) };
}

export async function uploadApplicationDocument(
  uuid: string,
  opts: {
    documentTypeId: number;
    file: File;
    businessTypeId?: number | null;
    shareholderUuid?: string;
    documentUuid?: string | null;
    metadata?: Record<string, string>;
    pdfPassword?: string | null;
  },
): Promise<unknown> {
  const form = new FormData();
  form.append("document_type_id", String(opts.documentTypeId));
  form.append("file", opts.file);
  if (opts.businessTypeId) {
    form.append("business_type_id", String(opts.businessTypeId));
  }
  if (opts.shareholderUuid) {
    form.append("shareholder_uuid", opts.shareholderUuid);
  }
  if (opts.documentUuid) {
    form.append("document_uuid", opts.documentUuid);
  }
  if (opts.metadata) {
    form.append("metadata", JSON.stringify(opts.metadata));
  }
  if (opts.pdfPassword) {
    form.append("pdf_password", opts.pdfPassword);
  }

  return api.post(partnerEndpoints.documents(uuid), form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteApplicationDocumentFile(
  applicationUuid: string,
  opts: { documentUuid: string; documentFileUuid: string },
): Promise<unknown> {
  return api.delete(partnerEndpoints.documents(applicationUuid), {
    data: {
      document_uuid: opts.documentUuid,
      document_file_uuid: opts.documentFileUuid,
    },
  });
}

export async function createDocumentMetadata(
  applicationUuid: string,
  opts: {
    documentTypeId: number;
    metadata: Record<string, string>;
  },
): Promise<{ document_uuid: string; metadata: Record<string, string> }> {
  const form = new FormData();
  form.append("document_type_id", String(opts.documentTypeId));
  form.append("update_metadata", "true");
  form.append("metadata", JSON.stringify(opts.metadata));
  return api.post(partnerEndpoints.documents(applicationUuid), form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteDocumentContainer(
  applicationUuid: string,
  documentUuid: string,
): Promise<unknown> {
  return api.delete(partnerEndpoints.documents(applicationUuid), {
    data: {
      document_uuid: documentUuid,
      delete_container: true,
    },
  });
}
